import { CLIPPING_LAYER, SHADOW_GEOMETRY_LAYER } from "gfx/layerConstants";
import type { THREECamera } from "gfx/THREECamera";
import type { Intersection, Object3D, Scene, Plane, OrthographicCamera } from "three";
import { Raycaster, Vector2, Vector3 } from "three";

/**
 * Helpers for easier raycasting.
 */
export class THREERaycastHelper {
  private readonly raycaster = new Raycaster();

  // Allocated ahead of time to avoid extra gc
  private raycastNdcCoordinates: Vector2 = new Vector2();
  private raycastOrigin: Vector3 = new Vector3();
  private raycastDir: Vector3 = new Vector3();

  constructor() {
    this.raycaster.layers.enableAll();
    this.raycaster.layers.disable(SHADOW_GEOMETRY_LAYER);
    this.raycaster.layers.disable(CLIPPING_LAYER);
  }

  private filterRaycastIntersections(ints: Intersection[]) {
    return ints.filter((x) => x.object.visible);
  }

  /**
   * @param ndcCoordinatesX Coordinates range from -1 to 1
   * @param ndcCoordinatesY Coordinates range from -1 to 1
   * @param camera
   * @param objects Objects to raycast against
   * @returns Intersected objects
   */
  raycastFromCursor(
    ndcCoordinatesX: number,
    ndcCoordinatesY: number,
    camera: THREECamera,
    objects: Object3D[],
  ): Intersection[] {
    this.rayFromCamera(ndcCoordinatesX, ndcCoordinatesY, camera, true);
    const ints = this.raycaster.intersectObjects(objects, true);
    return this.filterRaycastIntersections(ints);
  }

  raycastFromCursorToPlane(
    ndcCoordinatesX: number,
    ndcCoordinatesY: number,
    camera: THREECamera,
    plane: Plane,
  ): Vector3 | null {
    this.rayFromCamera(ndcCoordinatesX, ndcCoordinatesY, camera, false);
    const intersectPoint = new Vector3();
    return this.raycaster.ray.intersectPlane(plane, intersectPoint);
  }

  raycastWorld(
    scene: Scene,
    x: number,
    y: number,
    z: number,
    directionX: number,
    directionY: number,
    directionZ: number,
  ): Intersection[] {
    this.raycastOrigin.set(x, y, z);
    this.raycastDir.set(directionX, directionY, directionZ);
    return this.raycastWorldObjectInternal(this.raycastOrigin, this.raycastDir, scene);
  }

  raycastForElevationOcclusion(
    objects: Object3D[],
    x: number,
    y: number,
    z: number,
    directionX: number,
    directionY: number,
    directionZ: number,
    rayLengthSquared: number,
  ) {
    this.raycastOrigin.set(x, y, z);
    this.raycastDir.set(directionX, directionY, directionZ);
    return this.intersectObjectsFirstHit(objects, rayLengthSquared);
  }

  raycastWorldTowardsCamera(scene: Scene, x: number, y: number, z: number, camera: THREECamera): Intersection[] {
    this.raycastOrigin.set(x, y, z);
    if ((camera as OrthographicCamera).isOrthographicCamera) {
      camera.getWorldDirection(this.raycastDir);
      this.raycastDir.negate();
    } else {
      this.raycastDir.subVectors(camera.position, this.raycastOrigin);
      this.raycastDir.normalize();
    }
    this.raycaster.near = 0;
    this.raycaster.far = camera.position.distanceTo(this.raycastOrigin);
    return this.raycastWorldObjectInternal(this.raycastOrigin, this.raycastDir, scene);
  }

  private rayFromCamera(ndcCoordinatesX: number, ndcCoordinatesY: number, camera: THREECamera, useNearFar: boolean) {
    this.raycastNdcCoordinates.set(ndcCoordinatesX, ndcCoordinatesY);
    this.raycaster.setFromCamera(this.raycastNdcCoordinates, camera);
    if (useNearFar) {
      this.raycaster.near = camera.near;
      this.raycaster.far = camera.far;
    } else {
      this.raycaster.near = 0;
      this.raycaster.far = Infinity;
    }
  }

  private raycastWorldObjectInternal(raycastOrigin: Vector3, raycastDir: Vector3, object: Object3D): Intersection[] {
    this.raycaster.set(raycastOrigin, raycastDir);
    const ints = this.raycaster.intersectObject(object);
    return this.filterRaycastIntersections(ints);
  }

  private intersectObjectsFirstHit(objects: Object3D[], rayLengthSquared: number): boolean {
    for (const object of objects) {
      const ints = this.raycastWorldObjectInternal(this.raycastOrigin, this.raycastDir, object);
      for (const int of ints) {
        // return early if an occlusion is found
        if (int.distance * int.distance < rayLengthSquared) {
          return true;
        }
      }
      // otherwise, search recursively for more intersections
      if (object.children.length > 0) {
        this.intersectObjectsFirstHit(object.children, rayLengthSquared);
      }
    }

    return false;
  }
}
